/**
 * Инструкция «С чего начать» (БЗ-31, поручение владельца 27.10.2026): что человеку сделать с первого входа, что
 * заполнить и проверить, и что делать каждый день. Собирается из прав человека (как инструкция по роли, БЗ-02),
 * уровня денег, вида участия (сотрудник или внешний участник) и настроек компании (час утренней сводки, срок
 * отчёта смены). Одна сборка — для письма-приглашения, страницы «С чего начать» и PDF.
 *
 * В инструкции только то, что человек увидит в портале: шаг или дело появляется, если открыт раздел и есть право
 * на действие в той области, где его проверяет сервер (настройки компании — право на всю компанию, справочник
 * поставщиков — на компанию или юрлицо); ссылка — только в открытый раздел и на страницу, которая человеку
 * доступна. Сумм и цифр денег в тексте нет.
 */
import { z } from 'zod';
import { canAnywhere, canCompanyWide, canTenant, seesMoney } from './access';
import { EXTERNAL_PROFILES, type ExternalProfile } from './external-access';
import { EXPIRY_WARNINGS } from './licensing';
import type { ModuleCode } from './modules';
import type { Access, MoneyLevel } from './schemas';
import { RoleGuide, SLA_BLOCKING_HOURS, SLA_NORMAL_WORKING_HOURS } from './support';
import { buildRoleGuide, visibleModules } from './support-help';

export const StartLink = z.object({ title: z.string(), path: z.string() });
export type StartLink = z.infer<typeof StartLink>;

export const StartStep = z.object({
  key: z.string(),
  title: z.string(),
  text: z.string(),
  links: z.array(StartLink),
  /** Портал отмечает шаг сам (принятие приглашения, вход без пароля, экскурсия, уведомления) */
  auto: z.boolean(),
  /** Шаг сделан: на странице — по данным портала или отметке человека; в письме — всегда false */
  done: z.boolean(),
});
export type StartStep = z.infer<typeof StartStep>;

export const START_WHEN = {
  morning: 'Утром',
  day: 'В течение дня',
  evening: 'В конце дня',
  regular: 'Время от времени',
} as const;
export type StartWhen = keyof typeof START_WHEN;

export const StartDaily = z.object({
  key: z.string(),
  when: z.enum(Object.keys(START_WHEN) as [StartWhen, ...StartWhen[]]),
  title: z.string(),
  text: z.string(),
  links: z.array(StartLink),
});
export type StartDaily = z.infer<typeof StartDaily>;

export const StartGuide = z.object({
  company: z.string(),
  /** Роли через запятую: «Прораб, мастер» */
  roles: z.string(),
  /** Где действуют роли: «вся компания», юрлица, направления, проекты */
  where: z.string(),
  kind: z.enum(['staff', 'external']),
  /** Кто это: «сотрудник компании», «владелец компании», «внешний участник: поставщик» */
  who: z.string(),
  intro: z.string(),
  /** Что человек видит из сумм — словами, без цифр */
  money: z.string(),
  firstSteps: z.array(StartStep),
  daily: z.array(StartDaily),
  /** Разделы, открытые человеку: описание, что доступно, ссылка (БЗ-02) */
  modules: RoleGuide.shape.modules,
  /** Куда идти за помощью */
  help: z.array(z.string()),
});
export type StartGuide = z.infer<typeof StartGuide>;

/** Ответ страницы «С чего начать»: инструкция для текущего человека с отметками шагов */
export const StartView = StartGuide.extend({
  doneCount: z.number().int(),
  totalCount: z.number().int(),
});
export type StartView = z.infer<typeof StartView>;

export const StartStepInput = z.object({
  key: z.string().trim().min(1).max(40),
  done: z.boolean(),
});
export type StartStepInput = z.infer<typeof StartStepInput>;

export interface StartGuideContext {
  /** Права человека с учётом тарифа и демо — те же, что у него будут в портале */
  access: Access;
  /** Роли через запятую: «Прораб, мастер» */
  roleNames: string;
  /** Где действуют роли: «вся компания» или проекты и юрлица */
  where?: string | null;
  company: string;
  kind: 'staff' | 'external';
  externalProfile?: ExternalProfile | null;
  /** Станет владельцем компании (приглашение первого администратора) */
  isOwner?: boolean;
  /** Час утренней сводки компании по умолчанию, 0–23 */
  digestHour: number;
  /** Утренняя сводка в компании включена; не указано — включена */
  digestEnabled?: boolean;
  /** Срок сдачи отчёта смены, час 0–23 */
  reportHour: number;
  /** У человека уже есть вход в портал: приглашение он подтверждает своим паролем */
  existingUser?: boolean;
  /** Инструкция для вошедшего человека (страница «С чего начать»): приглашение уже принято */
  signedIn?: boolean;
  /** Компанию создал сам человек с главной страницы: просить новую ссылку не у кого */
  selfSignup?: boolean;
  /** Кто пригласил — для внешних участников это контакт по вопросам доступа */
  inviterName?: string | null;
  /** Каналы утренней сводки, подключённые в портале; не указано — подключены */
  mail?: boolean;
  telegram?: boolean;
}

const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

/** Название в кавычках «ёлочках», если своих кавычек в нём нет: «ТОО «Альфа»» не получится */
export const quoteName = (name: string) => (/[«»"„“]/.test(name) ? name : `«${name}»`);

/** Коды требований ТЗ в описаниях разделов («(БЗ-04)») людям в инструкции не нужны */
const withoutCodes = (text: string) => text.replace(/\s*\((?:[А-ЯЁA-Z]{2,3}-\d{2,3}(?:,\s*)?)+\)/g, '');

/** Перечень с «и» перед последним: «сметы, график и закупки» */
const listRu = (items: ReadonlyArray<string | number>) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} и ${items[items.length - 1]}`;
const lastDigit = (n: number) => (n % 100 >= 11 && n % 100 <= 14 ? 0 : n % 10);
/** «за 3 дня», «за 14 дней», «за 1 день» */
const daysWord = (n: number) =>
  lastDigit(n) === 1 ? 'день' : [2, 3, 4].includes(lastDigit(n)) ? 'дня' : 'дней';
/** «в течение 2 часов», «в течение 1 часа» */
const hoursGen = (n: number) => (lastDigit(n) === 1 ? 'часа' : 'часов');
const workHoursGen = (n: number) => (lastDigit(n) === 1 ? 'рабочего часа' : 'рабочих часов');

const MONEY_PHRASES: Record<MoneyLevel, string> = {
  all: 'Вам открыты все суммы: бюджет и маржа, договоры, счета и оплаты.',
  acts_invoices: 'Из сумм вам видны суммы актов и счетов, остальные суммы скрыты.',
  acts: 'Из сумм вам видны суммы актов, остальные суммы скрыты.',
  warehouse: 'Из сумм вам видна стоимость на складе, остальные суммы скрыты.',
  none: 'Суммы вам не показываются: там, где они есть, портал пишет «сумма скрыта».',
};

/**
 * Разделы только для сотрудников компании: сервер отвечает внешним участникам «Площадка доступна сотрудникам
 * компании», видеонаблюдения во внешнем доступе нет. Из «Площадки» внешним открыты только акты на допработы —
 * они даются отдельным шагом заказчику и технадзору. Признак в общем списке разделов — запрос потоку 1.
 */
const STAFF_ONLY_MODULES: ReadonlySet<string> = new Set<ModuleCode>(['site', 'video']);

/**
 * Разделы без своей страницы: бюджет — вкладка карточки проекта, у Pilot страницы пока нет (адрес раздела ведёт
 * на заглушку «раздел появится»). Ссылка в инструкции — только на работающую страницу.
 */
const MODULE_PAGES: Partial<Record<ModuleCode, { path: string; note: string } | null>> = {
  budget: { path: '/projects', note: 'Открывается во вкладке «Бюджет и маржа» карточки проекта.' },
  ai: null,
};

/** Разделы, которые руководитель раздаёт сотрудникам, — в винительном падеже для текста шага */
const DELEGATED: Array<[ModuleCode, string]> = [
  ['estimates', 'сметы'],
  ['schedule', 'график'],
  ['procurement', 'закупки'],
  ['contracts', 'договоры'],
  ['warehouse', 'склад'],
  ['site', 'площадку'],
  ['qhse', 'качество и охрану труда'],
];

/**
 * Инструкция «С чего начать» для человека с такими правами. Порядок первых шагов — от входа к своим разделам:
 * принять приглашение, войти с телефона, экскурсия, профиль и уведомления, затем то, что заполнить и проверить
 * по своей работе. Порядок дня — утро, день, вечер.
 */
export function buildStartGuide(ctx: StartGuideContext): StartGuide {
  const a = ctx.access;
  const staff = ctx.kind === 'staff';
  const profile = staff ? null : (ctx.externalProfile ?? null);
  const visible = new Set<ModuleCode>(visibleModules(a).map((m) => m.code));
  // Право хоть в одной области — для разделов, где сервер проверяет право по проекту записи
  const has = (p: string) => canAnywhere(a, p);
  // Настройки, роли, приглашения, лимиты платежей, выгрузка в 1С — сервер пускает только по праву на всю компанию
  const tenantHas = (p: string) => canTenant(a, p);
  // Новый проект и справочник поставщиков — право на компанию или юрлицо
  const companyHas = (p: string) => canCompanyWide(a, p);
  const open = (code: ModuleCode) => visible.has(code) && (staff || !STAFF_ONLY_MODULES.has(code));
  const anyOf = (...perms: string[]) => perms.some(has);
  const L = (title: string, path: string, module?: ModuleCode): StartLink | null =>
    module && !open(module) ? null : { title, path };
  const links = (...list: Array<StartLink | null>): StartLink[] => list.filter((l): l is StartLink => !!l);
  const report = hh(ctx.reportHour);
  const digest = hh(ctx.digestHour);
  const inviter = ctx.inviterName?.trim() || null;
  const mail = ctx.mail ?? true;
  const telegram = ctx.telegram ?? true;
  // Компания выключила утреннюю сводку — о ней не пишем: ни шага настройки, ни «приходит сводка»
  const channels =
    ctx.digestEnabled === false
      ? null
      : mail && telegram
        ? 'на почту и в Telegram'
        : mail
          ? 'на почту'
          : telegram
            ? 'в Telegram'
            : null;
  const siteOpen = staff && open('site');
  // Владелец и администратор с полными правами на всю компанию: им — настройка компании и решения, а не работа
  // каждого раздела. Иначе в инструкцию попали бы обязанности прораба, кладовщика и снабженца сразу.
  // Администратор одного проекта или юрлица — не руководитель компании: ему — работа по его разделам.
  const leader =
    staff && (Boolean(ctx.isOwner) || ['admin.users', 'admin.roles', 'admin.settings'].every(tenantHas));
  const ops = !leader;

  const steps: StartStep[] = [];
  const step = (key: string, title: string, text: string, ls: StartLink[] = [], auto = false) =>
    steps.push({ key, title, text, links: ls, auto, done: false });

  /* ── Вход ── */
  step(
    'accept',
    ctx.selfSignup ? 'Задайте пароль и войдите' : 'Примите приглашение',
    ctx.signedIn
      ? `Вы вошли в портал компании ${quoteName(ctx.company)}.`
      : ctx.existingUser
        ? `У вас уже есть вход в портал. Откройте ссылку из письма и подтвердите приглашение своим паролем — компания ${quoteName(ctx.company)} появится в меню пользователя.`
        : `Откройте ссылку из письма, проверьте имя и фамилию и придумайте пароль не короче 10 символов. Ссылка срабатывает один раз; ${
            ctx.selfSignup
              ? 'если срок вышел, зарегистрируйтесь ещё раз на главной странице портала.'
              : 'если срок вышел, попросите прислать новую.'
          }`,
    [],
    true,
  );
  step(
    'devices',
    'Откройте портал на компьютере и на телефоне',
    'Портал работает в браузере, ставить ничего не нужно. На телефоне добавьте его на главный экран: на iPhone — «Поделиться» → «На экран „Домой“», на Android — меню браузера → «Добавить на главный экран». Так портал открывается как приложение.',
  );
  step(
    'passkey',
    'Включите вход по Face ID, отпечатку или Windows Hello',
    'Профиль → «Вход по Face ID и отпечатку»: добавьте ключ на каждом своём устройстве. Пароль после этого вводить не придётся.',
    links(L('Профиль', '/profile')),
    true,
  );
  if (staff)
    step(
      'tour',
      'Пройдите экскурсию по своим разделам',
      'При первом входе портал сам покажет короткую экскурсию — только по разделам, открытым вам. Пропустили — меню пользователя → «Экскурсия по порталу».',
      [],
      true,
    );
  step(
    'profile',
    'Проверьте свои данные и роли',
    ctx.isOwner
      ? 'Профиль → «Учётная запись»: имя и должность — их видят сотрудники и участники проектов.'
      : `Профиль → «Учётная запись»: имя, должность, роли и проекты. Если нет нужного проекта или раздела, напишите ${
          inviter ? `тому, кто вас пригласил (${inviter}),` : 'тому, кто вас пригласил,'
        } или администратору компании.`,
    links(L('Профиль', '/profile')),
  );
  if (open('inbox') && channels)
    step(
      'notifications',
      telegram ? 'Настройте утреннюю сводку и Telegram' : 'Настройте утреннюю сводку',
      `Профиль → «Уведомления»: включите «Утреннюю сводку моих дел» и выберите час (в компании по умолчанию ${digest})${
        telegram ? ', подключите Telegram одноразовым кодом' : ''
      }, затем нажмите «Отправить пробную сводку». Сводка приходит ${channels}: просроченное, срок сегодня и остальные дела со ссылками; суммы в письма и сообщения не попадают.${
        staff ? '' : ' Внешним участникам сводка приходит, только если включить её здесь.'
      }`,
      links(L('Профиль', '/profile')),
      true,
    );
  if (staff && open('calendar'))
    step(
      'calendar',
      'Подключите календарь к телефону',
      'Календарь → «Подписка для телефона»: дела со сроком, вехи проектов, поставки и платежи по договорам появятся в календаре телефона. В подписке нет сумм и текста дел. Шаг по желанию.',
      links(L('Календарь', '/calendar', 'calendar')),
    );

  /* ── Компания: владелец и администратор всей компании ── */
  if (staff && tenantHas('admin.settings'))
    step(
      'license',
      'Проверьте лицензию компании',
      `Администрирование → «Лицензия»: тариф, срок, число мест для сотрудников и разделы. О конце срока портал предупредит за ${listRu(EXPIRY_WARNINGS)} ${daysWord(EXPIRY_WARNINGS[EXPIRY_WARNINGS.length - 1]!)}.`,
      links(L('Лицензия', '/admin/license')),
    );
  if (staff && tenantHas('admin.org')) {
    step(
      'company',
      'Заполните юрлица группы и орг-структуру',
      'Администрирование → «Юрлица группы»: реквизиты каждого юрлица. «Орг-структура»: подразделения и сотрудники, список можно загрузить из Excel; из карточки сотрудника его можно пригласить в портал.',
      links(L('Юрлица группы', '/admin/legal-entities'), L('Орг-структура', '/admin/org')),
    );
    const settings = [
      'часовой пояс и час утренней сводки',
      siteOpen ? 'срок сдачи отчёта смены' : null,
      'предупреждения об обновлении портала',
      open('procurement') ? 'контроль дробления закупок' : null,
    ].filter((x): x is string => !!x);
    step(
      'settings',
      'Задайте настройки компании',
      `Администрирование → «Настройки»: ${settings.join(', ')}.`,
      links(L('Настройки', '/admin/settings')),
    );
  }
  if (staff && tenantHas('admin.roles'))
    step(
      'roles',
      'Проверьте роли и права',
      'Администрирование → «Роли и права»: что входит в каждую роль; две роли можно сравнить рядом. Уберите лишнее до того, как приглашать людей.',
      links(L('Роли и права', '/admin/roles')),
    );
  if (staff && tenantHas('admin.users'))
    step(
      'team',
      'Пригласите команду',
      'Администрирование → «Сотрудники» → «Пригласить»: почта, роли на компанию или проект. Доступ к деньгам выдаёт владелец компании. Подрядчиков, поставщиков и заказчика приглашайте как внешних участников с их организацией — они увидят только своё. Каждый получит письмо с такой же инструкцией под свои права.',
      links(L('Сотрудники', '/admin/members', 'admin')),
    );

  /* ── Проекты и подготовка данных ── */
  if (staff && companyHas('project.create'))
    step(
      'projects',
      'Заведите проекты и объекты',
      '«Портфель» → «Новый проект»: карточка, объекты, сроки и участники. Черновик мастера сохраняется на устройстве, его можно закончить позже.',
      links(L('Портфель', '/', 'portfolio')),
    );
  else if (ops && staff && has('project.edit') && open('overview'))
    step(
      'project-card',
      'Проверьте карточку проекта',
      'Карточка проекта: объекты, вехи, участники и блок «Требует внимания».',
      links(L('Проекты', '/projects', 'overview')),
    );
  if (ops && has('estimate.edit') && open('estimates'))
    step(
      'estimates',
      'Загрузите сметы проекта',
      '«Сметы»: импорт из KENML 1.0 или Excel по форме 4*, проверка и пересчёт уровня цен; выгрузка в KENML 2.01 для госэкспертизы.',
      links(L('Сметы', '/estimates', 'estimates')),
    );
  if (ops && has('norms.manage') && open('norms'))
    step(
      'norms',
      'Проверьте нормативную базу',
      '«Нормативная база»: выпуски, по которым считаются сметы, и список документов на проверку — расхождения с перечнями АГСК и пропуски в сериях.',
      links(L('Нормативная база', '/norms', 'norms'), L('На проверку', '/norms/library/reviews', 'norms')),
    );
  if (ops && has('schedule.edit') && open('schedule'))
    step(
      'schedule',
      'Загрузите или постройте график',
      `«График»: импорт MS Project XML, XER или P6 XML либо работы вручную, связи и календари.${
        has('schedule.baseline') ? ' Затем сохраните базовый план — с ним портал сравнивает факт.' : ''
      }`,
      links(L('График', '/schedule', 'schedule')),
    );
  if (ops && has('budget.edit') && open('budget'))
    step(
      'budget',
      'Проверьте бюджет проекта',
      'Карточка проекта → «Бюджет и маржа»: себестоимость по разделам и статьям, варианты исполнения с обоснованием выбора.',
      links(L('Проекты', '/projects', 'overview')),
    );
  // Справочник людей и техники — общий для компании: сервер пускает по праву на компанию или юрлицо
  if (ops && staff && companyHas('resources.edit') && open('resources'))
    step(
      'resources',
      'Заполните ресурсы',
      '«Ресурсы»: справочник людей и техники, вахты и персонал — от них считаются загрузка и табели.',
      links(L('Ресурсы', '/resources', 'resources')),
    );
  if (ops && staff && has('psd.upload') && open('psd'))
    step(
      'psd',
      'Загрузите комплекты ПСД',
      '«ПСД»: комплекты по шифру, листы и ревизии. Утверждённая ревизия получает штамп «В производство работ», рассылка идёт по накладной.',
      links(L('ПСД', '/psd', 'psd')),
    );
  if (ops && staff && has('documents.edit') && open('documents'))
    step(
      'documents',
      'Разложите документы проекта',
      '«Документы»: исходные файлы проекта с категориями доступа, реестр писем, исполнительная документация.',
      links(
        L('Документы', '/documents', 'documents'),
        L('Письма', '/documents/letters', 'documents'),
        L('Исполнительная', '/documents/exec', 'documents'),
      ),
    );

  /* ── Закупки, договоры, финансы ── */
  if (
    ops &&
    staff &&
    companyHas('suppliers.edit') &&
    open('suppliers') &&
    anyOf('procurement.rfq.manage', 'contracts.edit')
  )
    step(
      'suppliers',
      'Проверьте карточки поставщиков',
      '«Поставщики»: реквизиты, категории, документы со сроком действия. Новых поставщиков заводите здесь — по БИН портал найдёт похожие карточки и не даст завести дубль.',
      links(L('Поставщики', '/suppliers', 'suppliers')),
    );
  if (ops && staff && has('procurement.request.create') && open('procurement'))
    step(
      'request',
      'Создайте первую заявку на закуп',
      '«Закупки» → «Новая заявка»: проект, позиции, количество и нужный срок. Дальше заявку проверяют и передают снабжению; где она сейчас — видно в её карточке.',
      links(L('Закупки', '/procurement', 'procurement')),
    );
  if (ops && staff && has('procurement.rfq.manage') && open('procurement'))
    step(
      'rfq',
      'Разберитесь с запросами КП',
      '«Закупки» → «Запросы КП»: приглашение поставщиков, их ответы по позициям, сравнение предложений и выбор поставщика с пояснением.',
      links(L('Запросы КП', '/procurement?tab=rfqs', 'procurement')),
    );
  if (ops && staff && anyOf('procurement.registry', 'procurement.payment.request') && open('procurement'))
    step(
      'registries',
      'Проверьте реестры закупок и заявки на оплату',
      '«Закупки» → «Реестры» и «Заявки на оплату»: комплект документов к оплате, номера и данные для 1С.',
      links(
        L('Реестры', '/procurement?tab=registries', 'procurement'),
        L('Заявки на оплату', '/procurement?tab=payments', 'procurement'),
      ),
    );
  if (ops && staff && has('contracts.edit') && open('contracts'))
    step(
      'contracts',
      'Проверьте договоры и маршрут согласования',
      '«Договоры»: договор из выбранного КП или вручную, согласование по подразделениям, подписание, допсоглашения и документы по договору.',
      links(L('Договоры', '/contracts', 'contracts')),
    );
  // Комплекты документов к оплате и лимиты платежей — общие для всех юрлиц: их задаёт финансовый менеджер всей
  // компании; лимиты — ещё и с уровнем денег «Акты и счета»
  if (ops && staff && tenantHas('finance.manager') && open('finance')) {
    const limits = seesMoney(a, 'acts_invoices');
    step(
      'finance',
      limits ? 'Задайте комплекты документов к оплате и лимиты' : 'Задайте комплекты документов к оплате',
      `«Финансы» → «Комплекты документов к оплате»: какие документы нужны для оплаты по виду договора.${
        limits ? ' «Лимиты и сверка»: сколько можно платить в день и в неделю.' : ''
      }`,
      links(
        L('Комплекты документов', '/finance?tab=docs', 'finance'),
        limits ? L('Лимиты и сверка', '/finance?tab=limits', 'finance') : null,
      ),
    );
  }
  if (ops && staff && tenantHas('finance.export') && seesMoney(a, 'acts_invoices') && open('finance'))
    step(
      'finance-1c',
      'Проверьте выгрузку в 1С',
      '«Финансы» → «Выгрузка в 1С»: сопоставление полей и выгрузка заявок на оплату.',
      links(L('Выгрузка в 1С', '/finance?tab=export', 'finance')),
    );

  /* ── Площадка, склад, качество ── */
  // «Площадка» — только сотрудникам. Отчёт смены открыт и без права «Видеть отчёты площадки»: страница пускает
  // по праву сдавать отчёты
  if (ops && staff && has('site.report'))
    step(
      'site',
      'Откройте «Отчёт смены» на телефоне',
      `«Площадка» → «Отчёт смены»: план дня и отчёт за смену — объёмы, люди, техника, фото. Сдать отчёт нужно до ${report}. Без связи отчёт сохраняется на телефоне и уходит сам, когда связь появится.`,
      links(L('Отчёт смены', '/site'), L('Мой день', '/site/day')),
    );
  if (ops && staff && has('site.review'))
    step(
      'site-review',
      'Откройте проверку отчётов смены',
      `«Проверка отчётов»: принять отчёт и объёмы или вернуть с причиной. «Не сдали отчёт смены» — кто не сдал после ${report}.`,
      links(L('Проверка отчётов', '/site/review'), L('Не сдали отчёт', '/site/missing')),
    );
  if (ops && staff && has('warehouse.operate') && open('warehouse'))
    step(
      'warehouse',
      'Проверьте склады и остатки',
      '«Склад»: склады проекта, каталог ТМЦ, остатки на начало. Приход, перемещение и выдачу оформляйте сразу в портале, этикетки с QR печатаются из раздела.',
      links(L('Склад', '/warehouse', 'warehouse'), L('Этикетки', '/warehouse/labels', 'warehouse')),
    );
  if (ops && staff && has('qhse.edit') && open('qhse')) {
    // Шаблоны чек-листов правит тот, кто ведёт проверки по всей компании или юрлицу
    const templates = companyHas('qhse.edit');
    step(
      'qhse',
      templates ? 'Подготовьте чек-листы и наряды-допуски' : 'Проверьте наряды-допуски и инструктажи',
      templates
        ? '«Качество и ОТ»: шаблоны чек-листов приёмки, наряды-допуски, инструктажи и допуски работников.'
        : '«Качество и ОТ»: наряды-допуски, инструктажи и допуски работников по вашим проектам, приёмки по чек-листам.',
      links(
        templates ? L('Шаблоны чек-листов', '/qhse?tab=templates', 'qhse') : null,
        L('Наряды-допуски', '/qhse?tab=permits', 'qhse'),
      ),
    );
  }
  if (ops && staff && has('supervision.issue') && open('supervision'))
    step(
      'remarks',
      'Выдавайте замечания и предписания в портале',
      '«Надзор и вопросы»: замечание с фото, сроком и ответственным; исполнитель получает дело в «Ждёт меня», просрочки видны обеим сторонам.',
      links(L('Надзор и вопросы', '/supervision', 'supervision')),
    );
  if (leader) {
    const parts = DELEGATED.filter(([m]) => open(m)).map(([, t]) => t);
    step(
      'delegate',
      'Распределите работу по разделам',
      `${
        parts.length
          ? `${listRu(parts).replace(/^./, (c) => c.toUpperCase())} ведут сотрудники по своим ролям`
          : 'Работу в разделах ведут сотрудники по своим ролям'
      } — каждый получит в письме свой порядок работы. Разделы, открытые вам, описаны ниже. Решения, которые нужны от вас, соберутся в «Ждёт меня».`,
      links(L('Портфель', '/', 'portfolio'), L('Ждёт меня', '/inbox', 'inbox')),
    );
  }
  if (staff && open('analytics'))
    step(
      'analytics',
      'Откройте экран руководителя',
      '«Руководителю»: закупки, деньги, сроки и маржа по проектам на одной странице. «Отчёты и списки» → «Сводка в 08:00» — сводка руководителю на почту.',
      links(
        L('Руководителю', '/analytics', 'analytics'),
        L('Отчёты и списки', '/reports?tab=digest', 'reports'),
      ),
    );

  /* ── Внешние участники ── */
  if (profile === 'supplier' && has('procurement.view'))
    step(
      'cabinet',
      'Откройте кабинет поставщика',
      '«Кабинет поставщика»: запросы коммерческих предложений, на которые вас пригласили. Ответьте по позициям (цена, срок, комментарий) или приложите файл КП; отказаться можно с причиной.',
      links(L('Кабинет поставщика', '/suppliers/cabinet')),
    );
  if (profile === 'subcontractor' && anyOf('site.report', 'schedule.view'))
    step(
      'my-schedule',
      'Проверьте свой график',
      '«Мой график»: работы, где ваша организация — исполнитель: план, базовый план и факт. Отметьте выполнение — генподрядчик проверит и примет его в график.',
      links(L('Мой график', '/schedule/contractor')),
    );
  if ((profile === 'supplier' || profile === 'subcontractor') && open('contracts'))
    step(
      'own-contracts',
      'Проверьте свои договоры и документы',
      '«Договоры»: только договоры вашей организации, их документы и оплаты.',
      links(L('Договоры', '/contracts', 'contracts')),
    );
  // Заказчик, проектировщик, инспектор: текст шага собирается только из открытых им разделов
  const parts = (list: Array<[boolean, string, StartLink | null]>) => {
    const shown = list.filter(([ok, , l]) => ok && l);
    return { text: shown.map(([, t]) => t).join(' '), ls: links(...shown.map(([, , l]) => l)) };
  };
  // Акты на допработы — единственная страница «Площадки», открытая внешним: подписывают заказчик и технадзор
  const extraWorks = (profile === 'customer' || profile === 'inspector') && has('site.view');
  if (profile === 'customer') {
    const p = parts([
      [open('schedule'), '«График»: сроки работ и отставание от плана.', L('График', '/schedule')],
      [open('changes'), '«Изменения»: запросы на изменение по проекту.', L('Изменения', '/changes')],
      [
        extraWorks,
        '«Акты на допработы»: работы сверх договора за день; ваша подпись создаёт запрос на изменение.',
        L('Акты на допработы', '/site/extra-works'),
      ],
    ]);
    if (p.ls.length) step('customer', 'Посмотрите ход работ', p.text, p.ls);
  }
  if (profile === 'designer' || profile === 'inspector') {
    const p = parts([
      [
        open('supervision'),
        profile === 'designer'
          ? '«Надзор и вопросы» → «Вопросы к проекту»: ответы с файлами, журнал и замечания авторского надзора.'
          : '«Надзор и вопросы»: замечания и предписания с фото и сроком, вызовы на освидетельствование скрытых работ.',
        profile === 'designer'
          ? L('Вопросы к проекту', '/supervision?tab=questions')
          : L('Надзор и вопросы', '/supervision'),
      ],
      [
        open('psd'),
        has('psd.upload') ? '«ПСД»: загрузка новых ревизий листов.' : '«ПСД»: действующие ревизии листов.',
        L('ПСД', '/psd'),
      ],
      [
        extraWorks,
        '«Акты на допработы»: работы сверх договора за день; ваша подпись создаёт запрос на изменение.',
        L('Акты на допработы', '/site/extra-works'),
      ],
    ]);
    if (p.ls.length)
      step(
        profile,
        profile === 'designer'
          ? 'Откройте вопросы к проекту и ПСД'
          : 'Откройте замечания и освидетельствования',
        p.text,
        p.ls,
      );
  }

  /* ── Каждый день ── */
  const daily: StartDaily[] = [];
  const day = (key: string, when: StartWhen, title: string, text: string, ls: StartLink[] = []) =>
    daily.push({ key, when, title, text, links: ls });

  if (open('inbox'))
    day(
      'digest',
      'morning',
      channels ? 'Сводка и «Ждёт меня»' : '«Ждёт меня»',
      `${
        channels ? `В ${digest} (или в ваш час из профиля) приходит сводка ${channels}. ` : ''
      }Откройте «Ждёт меня»: сначала просроченное, затем срок сегодня. Дело исчезает из списка само, когда его сделали в своём разделе.`,
      links(L('Ждёт меня', '/inbox', 'inbox')),
    );
  if (profile === 'supplier')
    day(
      'cabinet',
      'morning',
      'Новые запросы КП',
      'Проверьте кабинет поставщика: новые приглашения и срок ответа по каждому запросу.',
      links(L('Кабинет поставщика', '/suppliers/cabinet')),
    );
  if (ops && staff && has('site.report'))
    day(
      'plan',
      'morning',
      'План дня на участке',
      '«Отчёт смены» → план дня: работы, люди, техника и поставки на сегодня. «Мой день» — неделя, поставки, наряды и замечания.',
      links(L('Отчёт смены', '/site'), L('Мой день', '/site/day')),
    );
  if (staff && open('analytics'))
    day(
      'head',
      'morning',
      'Экран руководителя',
      'Одна-две минуты: закупки, деньги, сроки и маржа по проектам; в портфеле — блок «Требует внимания».',
      links(L('Руководителю', '/analytics', 'analytics'), L('Портфель', '/', 'portfolio')),
    );
  if (ops && staff && has('finance.edit') && open('finance'))
    day(
      'pay-calendar',
      'morning',
      'Платёжный календарь дня',
      '«Финансы»: оплаты на сегодня в пределах лимита дня. Возможная повторная оплата стоит до решения финансового менеджера.',
      links(L('Финансы', '/finance', 'finance')),
    );

  if (ops && staff && has('procurement.request.review') && open('procurement'))
    day(
      'requests',
      'day',
      'Проверка заявок',
      'Новые заявки на закуп ждут вас в «Ждёт меня»: принять или вернуть с причиной.',
      links(L('Ждёт меня', '/inbox', 'inbox'), L('Закупки', '/procurement', 'procurement')),
    );
  if (ops && staff && has('procurement.rfq.manage') && open('procurement'))
    day(
      'rfq',
      'day',
      'Запросы КП',
      'Внесите ответы поставщиков, сравните предложения и выберите поставщика с пояснением.',
      links(L('Запросы КП', '/procurement?tab=rfqs', 'procurement')),
    );
  if (
    staff &&
    anyOf(
      'contracts.approve',
      'contracts.approve.accounting',
      'contracts.approve.legal',
      'contracts.approve.deputy',
      'contracts.approve.security',
      'changes.approve',
      'procurement.approve.limit',
      'budget.approve',
      'estimate.approve',
      'timesheet.approve',
    ) &&
    open('inbox')
  )
    day(
      'approvals',
      'day',
      'Согласования',
      'Договоры, изменения, выбор поставщика и другие решения, которые ждут вас, — в «Ждёт меня». Решение с причиной видят все участники маршрута.',
      links(L('Ждёт меня', '/inbox', 'inbox')),
    );
  if (staff && has('contracts.sign') && open('contracts'))
    day(
      'sign',
      'day',
      'Подписание договоров',
      'Договоры, прошедшие согласование, ждут подписи в «Ждёт меня».',
      links(L('Ждёт меня', '/inbox', 'inbox'), L('Договоры', '/contracts', 'contracts')),
    );
  if (ops && staff && has('procurement.payment.confirm') && open('procurement'))
    day(
      'paid',
      'day',
      'Факт оплаты',
      '«Закупки» → «Заявки на оплату»: отметьте оплаченные заявки с датой оплаты.',
      links(L('Заявки на оплату', '/procurement?tab=payments', 'procurement')),
    );
  if (ops && staff && has('warehouse.operate') && open('warehouse'))
    day(
      'warehouse',
      'day',
      'Приход и выдача',
      'Оформляйте поступление и выдачу в момент операции, с телефона; получатель подтверждает выдачу в портале.',
      links(L('Склад', '/warehouse', 'warehouse')),
    );
  if (ops && staff && has('warehouse.approve') && open('inbox'))
    day(
      'warehouse-decisions',
      'day',
      'Решения по складу',
      'Выдача сверх лимита, перерасход, потери и недостачи ждут вашего решения в «Ждёт меня».',
      links(L('Ждёт меня', '/inbox', 'inbox')),
    );
  if (ops && staff && has('site.review'))
    day(
      'review',
      'day',
      'Проверка отчётов смены',
      'Отчёты на проверке: примите объёмы или верните с причиной. Принятые объёмы идут в журнал работ.',
      links(L('Проверка отчётов', '/site/review')),
    );
  if (ops && has('supervision.respond') && open('supervision'))
    day(
      'respond',
      'day',
      'Замечания к вам',
      staff
        ? 'Ответьте на замечания и предписания до срока: фото устранения и комментарий.'
        : 'Ответьте на замечания и предписания, где ответственный — вы: фото устранения и комментарий до срока.',
      links(L('Надзор и вопросы', '/supervision', 'supervision')),
    );
  if (ops && has('supervision.issue') && open('supervision'))
    day(
      'issue',
      'day',
      'Замечания на месте',
      'Записывайте замечание сразу: фото, срок и ответственный. Устранение проверяйте по фото исполнителя.',
      links(L('Надзор и вопросы', '/supervision', 'supervision')),
    );
  if (ops && staff && has('qhse.edit') && open('qhse'))
    day(
      'qhse',
      'day',
      'Качество и охрана труда',
      'Приёмки по чек-листам, наряды-допуски и инструктажи — в день работ, до начала смены.',
      links(L('Качество и ОТ', '/qhse', 'qhse')),
    );
  if (ops && has('psd.upload') && open('psd'))
    day(
      'psd',
      'day',
      'ПСД и вопросы к проекту',
      // Накладные ведёт компания: внешнему проектировщику рассылка недоступна
      staff
        ? 'Новые ревизии и вопросы к проекту: проверьте, ответьте, разошлите по накладной.'
        : 'Ответьте на вопросы к проекту до срока; новые ревизии листов загружайте в «ПСД».',
      links(L('ПСД', '/psd', 'psd'), L('Вопросы к проекту', '/supervision?tab=questions', 'supervision')),
    );
  if (ops && staff && has('schedule.edit') && open('schedule'))
    day(
      'schedule',
      'day',
      'График',
      'Обновляйте факт выполнения и прогноз окончания. При сдвиге работы портал показывает, что сдвинется вслед за ней.',
      links(L('График', '/schedule', 'schedule')),
    );
  if (ops && staff && has('estimate.edit') && open('estimates'))
    day(
      'estimates',
      'day',
      'Сметы и изменения',
      'Сметы на проверке и запросы на изменение, которые меняют объёмы и цены.',
      links(L('Сметы', '/estimates', 'estimates'), L('Изменения', '/changes', 'changes')),
    );
  if (ops && staff && has('documents.edit') && open('documents'))
    day(
      'letters',
      'day',
      'Письма',
      '«Документы» → «Письма»: входящие со сроком ответа; ответ регистрируется с номером.',
      links(L('Письма', '/documents/letters', 'documents')),
    );
  if (profile === 'subcontractor' && anyOf('site.report', 'schedule.view'))
    day(
      'my-schedule',
      'day',
      'Мой график',
      'Отметьте выполнение работ за день — генподрядчик проверит и примет его в график.',
      links(L('Мой график', '/schedule/contractor')),
    );
  if (extraWorks)
    day(
      'extra-works',
      'day',
      'Акты на допработы',
      'Новые акты на работы сверх договора: проверьте и подпишите — по подписи появится запрос на изменение.',
      links(L('Акты на допработы', '/site/extra-works')),
    );

  if (ops && staff && has('site.report'))
    day(
      'report',
      'evening',
      `Отчёт смены до ${report}`,
      'Объёмы, люди, техника, фото. Без связи отчёт уйдёт сам, когда телефон поймает сеть. После срока портал напоминает руководителю, кто не сдал.',
      links(L('Отчёт смены', '/site')),
    );
  if (open('inbox'))
    day(
      'close',
      'evening',
      'Итог дня',
      'Проверьте «Ждёт меня»: срочное сделано, по остальному понятен срок.',
      links(L('Ждёт меня', '/inbox', 'inbox')),
    );

  if (staff && tenantHas('admin.users') && open('admin'))
    day(
      'invites',
      'regular',
      'Приглашения',
      '«Сотрудники»: кто ещё не принял приглашение, у кого пометка «Требуется настройка». Истёкшее приглашение — «Отправить заново».',
      links(L('Сотрудники', '/admin/members', 'admin')),
    );
  if (open('whatsnew'))
    day(
      'whatsnew',
      'regular',
      'Что нового',
      `После каждого обновления портала здесь коротко написано, что изменилось в ваших разделах${channels ? '; утром это приходит и в сводке' : ''}.`,
      links(L('Что нового', '/whats-new', 'whatsnew')),
    );

  /* ── Разделы и помощь ── */
  const role = buildRoleGuide({ access: a, roleNames: ctx.roleNames });
  const cabinet = profile === 'supplier' || profile === 'subcontractor';
  const modules = role.modules
    // Обращения в поддержку — только сотрудникам: внешнему участнику раздел «Поддержка» не поможет; разделы
    // только для сотрудников внешним не показываются
    .filter((m) => staff || (m.code !== 'support' && !STAFF_ONLY_MODULES.has(m.code)))
    .filter((m) => MODULE_PAGES[m.code as ModuleCode] !== null)
    .map((m) => {
      // Поставщику и подрядчику «Закупки» в меню ведут в кабинет поставщика — так же и здесь
      if (cabinet && m.code === 'procurement')
        return {
          ...m,
          title: 'Кабинет поставщика',
          path: '/suppliers/cabinet',
          summary:
            'Запросы коммерческих предложений, на которые пригласили вашу организацию: ответ по позициям или файл КП, отказ с причиной.',
          youCan: [],
          steps: [],
        };
      const page = MODULE_PAGES[m.code as ModuleCode];
      const summary = withoutCodes(m.summary);
      return {
        ...m,
        path: page ? page.path : m.path,
        summary: page ? `${summary} ${page.note}` : summary,
        steps: m.steps.map(withoutCodes),
      };
    });

  const where = ctx.where?.trim() || '';
  const who = ctx.isOwner
    ? 'владелец компании'
    : staff
      ? 'сотрудник компании'
      : `внешний участник${profile ? `: ${EXTERNAL_PROFILES[profile].toLowerCase()}` : ''}`;
  const help = staff
    ? [
        'Кнопка «Помощь» есть на каждой странице: там инструкция к этой странице для вашей роли и «Написать в поддержку».',
        `Если из-за ошибки стоит работа, нажмите «Сообщить» вверху страницы, выберите «Ошибка» и «Не могу работать»: первый ответ — в течение ${SLA_BLOCKING_HOURS} ${hoursGen(SLA_BLOCKING_HOURS)}, в остальных случаях — в течение ${SLA_NORMAL_WORKING_HOURS} ${workHoursGen(SLA_NORMAL_WORKING_HOURS)}.`,
        'Предложение по улучшению портала — «Поддержка» → «Предложения»: за него голосуют, а статус виден автору.',
        'Эта инструкция всегда под рукой: меню пользователя → «С чего начать».',
      ]
    : [
        `Вопросы по доступу и данным — ${inviter ? `${inviter} или ` : ''}контактному лицу компании ${quoteName(ctx.company)}.`,
        'Кнопка «Помощь» есть на каждой странице: там инструкция к этой странице.',
        'Эта инструкция всегда под рукой: меню пользователя → «С чего начать».',
      ];

  const company = quoteName(ctx.company);
  const lead = ctx.signedIn
    ? `Вы работаете в портале EPC Pilot AI компании ${company}.`
    : ctx.selfSignup
      ? `Компания ${company} создана в портале EPC Pilot AI.`
      : `Компания ${company} приглашает вас в портал EPC Pilot AI.`;
  return {
    company: ctx.company,
    roles: role.roles,
    where,
    kind: ctx.kind,
    who,
    intro: `${lead} Вы — ${who}. Роль: ${role.roles}.${where ? ` Где действует роль: ${where}.` : ''} Ниже — что сделать в первый день, что делать каждый день и какие разделы открыты именно вам.`,
    money: MONEY_PHRASES[seesMoney(a, 'all') ? 'all' : a.money] ?? MONEY_PHRASES.none,
    firstSteps: steps,
    daily,
    modules,
    help,
  };
}

/** Шаги, которые портал отмечает сам: по ним известно, сделано или нет */
export const AUTO_START_STEPS = ['accept', 'passkey', 'tour', 'notifications'] as const;
